import { useEffect, useCallback } from 'react';
import { useMutation } from '@apollo/react-hooks';

import { MarkInlineCommentsReadMutation } from '@confluence/inline-comments-queries';
import { useCommentsData, UnreadAction } from '@confluence/comments-data';
import { useUnreadInlineComments } from '@confluence/unread-comments';

import { useCommentsPanel } from './useCommentsPanel';

export const useUnreadComments = () => {
	const [{ commentIdsByAnnotationToMarkAsRead }, { setCommentIdsByAnnotationToMarkAsRead }] =
		useCommentsPanel();

	const [, { updateUnreadStatus }] = useCommentsData();

	const [
		{ readCommentsListState, unreadCommentsListState },
		{ updateReadCommentsListState, updateUnreadCommentsListState },
	] = useUnreadInlineComments();

	// TODO: handle error
	const [markInlineCommentsRead] = useMutation(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		MarkInlineCommentsReadMutation,
	);

	// call the mutation to mark the list of comment IDs as read
	const handleMarkCommentsAsRead = useCallback(() => {
		if (Object.keys(commentIdsByAnnotationToMarkAsRead).length > 0) {
			const commentIdsToMarkAsRead = Object.values(commentIdsByAnnotationToMarkAsRead).reduce<
				string[]
			>((acc, set) => {
				acc.push(...set);
				return acc;
			}, []);

			void markInlineCommentsRead({
				variables: { commentIds: commentIdsToMarkAsRead },
			});

			// update our data store to indicate these comments are now read
			updateUnreadStatus(commentIdsByAnnotationToMarkAsRead, UnreadAction.READ);

			// reset our list of comments to mark as read
			setCommentIdsByAnnotationToMarkAsRead({ clearList: true });

			// update existing unread and read data stores
			const commentIdsToMarkAsReadSet = new Set(commentIdsToMarkAsRead);
			const filteredUnreadComments = unreadCommentsListState.filter(
				(unreadComment) => !commentIdsToMarkAsReadSet.has(unreadComment.id),
			);
			updateUnreadCommentsListState(() => filteredUnreadComments);

			const newReadCommentsListState = new Set([
				...readCommentsListState,
				...commentIdsToMarkAsRead,
			]);
			updateReadCommentsListState(() => newReadCommentsListState);
		}
	}, [
		markInlineCommentsRead,
		setCommentIdsByAnnotationToMarkAsRead,
		commentIdsByAnnotationToMarkAsRead,
		updateUnreadStatus,
		readCommentsListState,
		unreadCommentsListState,
		updateReadCommentsListState,
		updateUnreadCommentsListState,
	]);

	useEffect(() => {
		const handleBeforeUnload = () => {
			// Mark comments as read if the user closes the window
			handleMarkCommentsAsRead();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			// Mark comments as read when the component unmounts
			handleMarkCommentsAsRead();

			window.removeEventListener('beforeunload', handleBeforeUnload);
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		handleMarkCommentsAsRead,
	};
};
